import { TypeSystem } from "../../support/type-system.support";
import { ObservationDebtType } from "../dto/observation-debt-type.enum";
import { OrderStatus } from "../dto/order-status.enum";
import { User } from "../dto/user.model";
import { OrderGiftChoice } from "./order-gift-choice.model";
import { OrderItem } from "./order-item.model";
import { OrderTracking } from "./order-tracking.model";
import { OrderEvent } from "../dto/order-event.model";
import { Optional } from "../../support/optional.model";

	/**
	 * @description
	 *  Modelo de un pedido para el frontend.
	 *
	 * @see gigot-backend/ViewModels/Order/OrderDto.cs
	 */

export class Order {

	public applicationUser: User;
	public campaignId: number;
	public campaignNumber: string;
	public excemptTaxAmount: number;
	public grossIncomeLabel: string;
	public grossIncomePercentageAmount: number;
	public grossIncomeTaxAmount: number;
	public id: number;
	public invoiceTypeLabel: string;
	public netAmount: number;
	public observationDebtType: ObservationDebtType;
	public orderDate: Date;
	public orderGiftChoices: OrderGiftChoice[];
	public orderItems: OrderItem[];
	public orderStatus: OrderStatus;
	public orderTracking: OrderTracking[];
	public shippingAmount: number;
	public shippingLabel: string;
	public totalAmount: number;
	public vatAmount: number;
	public ivaNoCatTaxAmount: number;

	/**
	 * @description
	 *	Construye un pedido con una semántica vacía. Nótese que no es lo mismo
	 *	que construir un pedido con valores por defecto. Las reglas generales
	 *	de construcción son:
	 *
	 *      - Campos 'array' se definen como [].
	 *      - Campos 'boolean' se definen como 'false'.
	 *      - Campos 'Date' se definen como 'undefined'.
	 *      - Campos 'enum' se definen como 'undefined'.
	 *      - Campos 'number' se definen como 'undefined'.
	 *      - Campos 'string' se definen como '' (cadena vacía).
	 *      - Campos 'object' se definen llamando a su propio método #empty().
	 *      - Campos de la superclase se definen al igual que campos 'object'.
	 *      - Campos genéricos se definen como 'undefined'.
	 *		- Campos recursivos se definen como 'undefined'.
	 *
	 * @returns
	 *	Un pedido vacío.
	 */
	public static empty(): Order {
		const order: Order = new Order();
		order.applicationUser = User.empty();
		order.campaignId = undefined;
		order.campaignNumber = "";
		order.excemptTaxAmount = undefined;
		order.grossIncomeLabel = "";
		order.grossIncomePercentageAmount = undefined;
		order.grossIncomeTaxAmount = undefined;
		order.id = undefined;
		order.invoiceTypeLabel = "";
		order.netAmount = undefined;
		order.observationDebtType = undefined;
		order.orderDate = undefined;
		order.orderGiftChoices = [];
		order.orderItems = [];
		order.orderStatus = undefined;
		order.orderTracking = [];
		order.shippingAmount = undefined;
		order.shippingLabel = "";
		order.totalAmount = undefined;
		order.vatAmount = undefined;
		order.ivaNoCatTaxAmount = undefined;
		return order;
	}

	/**
	 * @description
	 *  Método de reificación de un modelo compatible anémico. Esto permite
	 *  ejecutar lógica de negocios sobre el modelo generado, siendo que el
	 *  valor de sus propiedades se mantiene.
	 *
	 * @param anemicOrder
	 *  El modelo anémico a reificar.
	 *
	 * @returns
	 *  Devuelve un modelo de esta clase, cuyas propiedades fueron copiadas en
	 *  profundidad desde el modelo anémico.
	 */
	public static from(anemicOrder: Order): Order {
		if (!anemicOrder) return undefined;
		const order: Order = Order.empty();
		order.applicationUser = User.from(anemicOrder.applicationUser);
		order.campaignId = anemicOrder.campaignId;
		order.campaignNumber = anemicOrder.campaignNumber;
		order.excemptTaxAmount = anemicOrder.excemptTaxAmount;
		order.grossIncomeLabel = anemicOrder.grossIncomeLabel;
		order.grossIncomePercentageAmount = anemicOrder.grossIncomePercentageAmount;
		order.grossIncomeTaxAmount = anemicOrder.grossIncomeTaxAmount;
		order.id = anemicOrder.id;
		order.invoiceTypeLabel = anemicOrder.invoiceTypeLabel;
		order.netAmount = anemicOrder.netAmount;
		order.observationDebtType = anemicOrder.observationDebtType;
		order.orderDate = new Date(anemicOrder.orderDate);
		order.orderGiftChoices = TypeSystem.orElse(anemicOrder.orderGiftChoices, [])
			.map(orderGiftChoice => OrderGiftChoice.from(orderGiftChoice));
		order.orderItems = TypeSystem.orElse(anemicOrder.orderItems, [])
			.map(orderItem => OrderItem.from(orderItem));
		order.orderStatus = anemicOrder.orderStatus;
		order.orderTracking = TypeSystem.orElse(anemicOrder.orderTracking, [])
			.map(orderTracking => OrderTracking.from(orderTracking));
		order.shippingAmount = anemicOrder.shippingAmount;
		order.shippingLabel = anemicOrder.shippingLabel;
		order.totalAmount = anemicOrder.totalAmount;
		order.vatAmount = anemicOrder.vatAmount;
		order.ivaNoCatTaxAmount = anemicOrder.ivaNoCatTaxAmount;
		return order;
	}

	/**
	 * @description
	 *	Aplica una nota sobre este pedido, eliminando los ítems que requieren
	 *	cambio, reemplazo o acreditación. Los ítems se eliminan comenzando por
	 *	aquellos que poseen precio de promoción. Si el ítem finaliza sin
	 *	cantidad, entonces se elimina del pedido por completo.
	 *
	 * @param orderEvent
	 *	La nota con los ítems a descontar de este pedido.
	 *
	 * @returns
	 *	Este mismo pedido, modificado.
	 */
	public applyOrderEvent(orderEvent: OrderEvent): Order {
		orderEvent.orderEventItems
			.forEach(orderEventItem => {
				const index = this.orderItems
					.findIndex(orderItem => orderItem.id === orderEventItem.orderItemId);
				if (0 <= index) {
					const orderItem = this.orderItems[index];
					orderItem.promotionPriceQuantity -= Math
						.min(orderItem.promotionPriceQuantity, orderEventItem.quantity);
					orderItem.quantity -= orderEventItem.quantity;
					if (orderItem.isEmpty()) {
						this.orderItems.splice(index, 1);
					}
				}
			});
		return this;
	}

	public findOrderItem(orderItemId: number): Optional<OrderItem> {
		return Optional.of(this.orderItems
			.find(orderItem => orderItem.id === orderItemId));
	}

	public findOrderItemByCode(code: string): Optional<OrderItem> {
		return Optional.of(this.orderItems
			.find(orderItem => orderItem.productCode === code));
	}

	public totalAmountAtBrochurePrice(): number {
		return this.orderItems
			.map(orderItem => orderItem.totalBrochurePrice())
			.reduce((accumulator, totalBrochurePrice) => accumulator + totalBrochurePrice, 0);
	}

	public totalAmountAtInvoicePrice(): number {
		return this.orderItems
			.map(orderItem => orderItem.totalInvoicePrice())
			.reduce((accumulator, totalInvoicePrice) => accumulator + totalInvoicePrice, 0);
	}
}
